/**
 * Building the audio a clip plays through: the Tone voice for an instrument preset, its effect
 * chain, and the master limiter they all end at.
 *
 * Lifted out of useMidiPlayer unchanged when a second player — the chord-bar preview — needed the
 * same instruments. Nothing here knows what is being played or when; that is each player's own
 * business. What they share is everything between picking an instrument and having something to
 * call triggerAttackRelease on, which is the part with the sample downloads, the fallbacks and the
 * levels in it.
 */

import { getInstrument, getInstrumentName, isSampledInstrument, isSustainedInstrument } from './midiInstruments';
import { loadInstrumentBuffers } from './sampleLibrary';
import { createLoopingSampler } from './loopingSampler';
import { useMetronomeStore } from '../pages/View/components/plugins/MetronomePlugin/MetronomePlugin';
import { scheduleClickSound } from './metronomeClick';

// Tone weighs ~200KB, so it's pulled in on the first play instead of at import time and never
// lands in the main bundle. The promise is cached so later plays are instant.
let tonePromise = null;

export const loadTone = () => {
  if (!tonePromise) tonePromise = import('tone');

  return tonePromise;
};

// Fallback headroom for a preset that doesn't set its own level
const SYNTH_VOLUME_DB = -10;

// The master chain every voice plays through: voice -> limiter -> speakers.
//
// There is no level control on this: playback sits at the one level every preset's own `volume`
// is tuned against, which is what a listener who never touched the old slider already heard. A
// clip that comes out too loud or too quiet is a preset to adjust in midiInstruments, not a knob
// for the listener — the device's own volume is the control they reach for anyway.
//
// The limiter is what makes those preset levels safe to set as high as they are. They put
// ordinary playing near unity, which leaves a dense chord briefly over the top; the limiter
// catches those peaks instead of letting them clip.
const LIMITER_THRESHOLD_DB = -1;

// Velocity arrives from the MIDI file as a 0-1 gain multiplier and Tone applies it literally, so
// a file written at a middling velocity 64 plays at half amplitude — a silent -6dB before any
// preset level is even considered, which is most of why clips needed the system volume turned
// up. Compressing the range onto [VELOCITY_FLOOR, 1] keeps loud notes louder than soft ones
// while lifting the whole clip; the floor is the gain the very softest note now gets.
const VELOCITY_FLOOR = 0.55;

export const curveVelocity = (velocity) => VELOCITY_FLOOR + (1 - VELOCITY_FLOOR) * velocity;

// Let the last note ring out before tearing the synth down, for a preset that doesn't say how
// long its own tail is
export const RELEASE_TAIL = 0.5;
// A note rounded down to a zero-length duration makes triggerAttackRelease throw, which kills
// every note after it in the clip. MIDI files do contain note-on/note-off on the same tick.
export const MIN_NOTE_DURATION = 0.01;
// How long a note sounds, which is not how long its key was held: a pedalled note carries a
// `sustain` that rings on past the key coming up. Notes from an unpedalled clip — and every clip
// uploaded before the pedal was read at all — carry no `sustain` and fall back to their own length.
export const getRingTime = (note) => Math.max(note.sustain || note.duration, MIN_NOTE_DURATION);

/**
 * Bring the audio context out of a suspended state, and report whether it worked.
 *
 * Tone.start() is nothing more than a wrapper around AudioContext.resume(), which browsers only
 * honour off the back of a user gesture. Returning the resulting state matters because a context
 * that never started is otherwise indistinguishable from a clip that makes no sound.
 */
export const resumeContext = async (Tone) => {
  const context = Tone.getContext();

  await Tone.start();

  return context.state === 'running';
};

const applyVolume = (voice, preset) => {
  voice.volume.value = typeof preset.volume === 'number' ? preset.volume : SYNTH_VOLUME_DB;

  return voice;
};

/**
 * Build the Tone voice for a synth preset. Presets name their voice class as a string so the data
 * stays plain JSON; an unknown name falls back to the basic synth rather than throwing.
 */
const createSynth = (Tone, preset, destination) => {
  const VoiceClass = Tone[preset.synth] || Tone.Synth;
  const synth = new Tone.PolySynth(VoiceClass).connect(destination);

  // Applied with set() rather than through the constructor: the constructor shallow-merges, so a
  // partial `envelope` would replace the voice's full default envelope — dropping the curve
  // properties and leaving a synth that runs but makes no sound. set() deep-merges.
  synth.set(preset.options);

  return applyVolume(synth, preset);
};

/**
 * The effect chain a preset plays through, sitting between its voice and the master limiter.
 *
 * Effects are named as strings for the same reason a preset names its voice class that way — the
 * instrument data stays plain JSON. The array is written in signal order (voice first, speakers
 * last) and built back to front, so each node connects into the one that follows it.
 *
 * @returns {Promise<{ input: object, nodes: Array }>} `input` is what the voice should connect to,
 *   which is the destination itself for a preset declaring no effects. Every node comes back in
 *   `nodes` because each one has to be disposed: an effect left connected to the master outlives
 *   the voice that fed it, exactly as the master chain does.
 */
const createEffectChain = async (Tone, preset, destination) => {
  const nodes = [];
  let input = destination;

  for (const { type, options } of [...(preset.effects || [])].reverse()) {
    const EffectClass = Tone[type];

    // A name Tone doesn't know drops that one effect rather than taking the whole preset down
    // with it — a lead without its delay is worth more to the listener than an instrument that
    // refuses to play at all
    if (!EffectClass) {
      console.warn(`Unknown effect "${type}" on preset ${preset.id}`);

      continue;
    }

    const node = new EffectClass(options).connect(input);

    // Chorus, Tremolo and the other LFO-driven effects pass audio through unmodulated until
    // their oscillator is running, which reads as the effect simply not being there
    if (typeof node.start === 'function') node.start();

    // Reverb renders its impulse response asynchronously, and notes fired before it lands come
    // out completely dry. Waiting here costs a few tens of milliseconds on the first play of a
    // reverb preset and nothing afterwards, since the voice is rebuilt per play but the wait
    // only ever delays the notes it belongs to.
    if (node.ready) await node.ready;

    nodes.push(node);
    input = node;
  }

  return { input, nodes };
};

/**
 * Build the Tone voice for a sampled preset.
 *
 * Not wrapped in a PolySynth — a Sampler is already polyphonic, and wrapping it would give every
 * voice its own copy of the buffers. The samples themselves are decoded once by sampleLibrary and
 * kept there, so a Sampler built from them costs nothing after the first play and can be disposed
 * with the rest of the audio state without throwing the download away.
 *
 * A sustaining instrument gets our own sampler rather than Tone's. Tone.Sampler plays each note
 * as a one-shot and every recording in the set is the same fixed length, so a held organ or pad
 * note stopped dead about three seconds in; createLoopingSampler loops the steady part instead.
 * Nothing else changes for it — same buffers, same release, same place in the chain.
 */
const createSampler = async (Tone, preset, destination) => {
  const buffers = await loadInstrumentBuffers(Tone, preset.id);

  const sampler = isSustainedInstrument(preset)
    ? createLoopingSampler(Tone, buffers, { release: preset.release }).connect(destination)
    : new Tone.Sampler({ urls: buffers, release: preset.release }).connect(destination);

  // The buffers are already decoded, so this resolves on the spot — it is here to close the gap
  // between constructing the Sampler and the transport firing the first note a few milliseconds
  // later, which would otherwise come out silent
  await Tone.loaded();

  return applyVolume(sampler, preset);
};

// What a failed download falls back to, so a clip is never silently unplayable. Deliberately not
// one of the synth presets from the JSON: the point is only to avoid silence, not to quietly hand
// the listener a different instrument from the one they picked and let it pass as the real thing.
const LAST_RESORT_PRESET = {
  name: 'a basic synth',
  synth: 'Synth',
  volume: -12,
  options: { oscillator: { type: 'triangle' } },
};

/**
 * The voice to play a clip with, the preset it actually ended up using, and the effect nodes
 * standing between them and the master gain.
 *
 * A download that fails drops back to a plain synth rather than playing nothing: the listener may
 * be offline or behind something blocking the CDN, and silence is indistinguishable from a broken
 * clip. The caller is told, so the fallback is visible rather than passed off as the real thing.
 */
export const createVoice = async (Tone, instrumentId, destination) => {
  const preset = getInstrument(instrumentId);

  // Built before the voice so the voice has something to connect into. Sampled presets declare no
  // effects today, which makes `input` the destination and this a no-op for them.
  const { input, nodes: effects } = await createEffectChain(Tone, preset, destination);

  if (!isSampledInstrument(preset)) return { voice: createSynth(Tone, preset, input), preset, effects };

  try {
    return { voice: await createSampler(Tone, preset, input), preset, effects };
  } catch (error) {
    console.error(`Falling back to a synth: ${preset.id} samples could not be loaded`, error);

    return {
      voice: createSynth(Tone, LAST_RESORT_PRESET, input),
      preset: LAST_RESORT_PRESET,
      effects,
      fellBackFrom: getInstrumentName(preset),
    };
  }
};

/**
 * The node a clip plays into, built fresh for each play and disposed with the voice — a Tone node
 * left connected to the destination survives the voice that fed it.
 */
export const createMaster = (Tone) => new Tone.Limiter(LIMITER_THRESHOLD_DB).toDestination();

/**
 * One metronome click, scheduled at an exact time on the Tone context.
 *
 * The same click as the metronome engine's own — deliberately, so a click heard next to a chord
 * preview is the click heard everywhere else in the app, down to the sound the user picked in
 * settings. What differs is only the clock: the
 * engine runs its own AudioContext, and a preview that mixed the two would have no way to keep
 * the click in phase with the notes. Sharing Tone's context is what makes them one performance
 * rather than two things started at nearly the same moment.
 *
 * @param {number} [volumeOverride] - Plays at this level instead of the metronome's own. A preview
 *   that carries its own volume control is balancing the click against the chords it is scheduled
 *   with, which is a different judgement from how loud the metronome should be over a whole band —
 *   so it sets the level for itself rather than moving the setting the rest of the app plays at.
 */
export const scheduleMetronomeClick = (Tone, time, isDownbeat = false, volumeOverride = null) => {
  const { volume: settingVolume, soundType } = useMetronomeStore.getState();

  scheduleClickSound(Tone.getContext().rawContext, time, isDownbeat ? 'downbeat' : 'regular', {
    soundType,
    volume: volumeOverride ?? settingVolume,
  });
};
